import React, { useState, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronRight, Sparkles, Check } from 'lucide-react';
import { saveScheduleSettings as saveScheduleFirestore, isAuthenticated } from '../../services/firestoreSync';

const ONBOARDING_KEY = 'adhd_onboarding_done';
const ONBOARDING_STEP_KEY = 'adhd_onboarding_step';

const CHALLENGES = [
  { id: 'start', label: 'Commencer les tâches', emoji: '🚀' },
  { id: 'remember', label: 'Se souvenir des choses', emoji: '🧠' },
  { id: 'focus', label: 'Rester concentré(e)', emoji: '🎯' },
  { id: 'time', label: 'Gérer le temps', emoji: '⏰' },
  { id: 'organize', label: "S'organiser", emoji: '📋' },
  { id: 'emotions', label: 'Gérer les émotions', emoji: '💙' },
];

const pageVariants = {
  enter: { opacity: 0, x: 30 },
  center: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -30 },
};

/**
 * OnboardingFlow - 6-step ADHD-optimized onboarding
 * Shows on first visit, resumable if interrupted.
 */
export function OnboardingFlow({ onComplete }) {
  const [step, setStep] = useState(() => {
    const saved = parseInt(localStorage.getItem(ONBOARDING_STEP_KEY) || '1', 10);
    return isNaN(saved) ? 1 : saved;
  });

  const [config, setConfig] = useState({
    wakeTime: 'normal',   // 'early' | 'normal' | 'late'
    sleepTime: 'normal',  // 'early' | 'normal' | 'late'
    challenges: [],
    takesMeds: null,      // null | true | false | 'skip'
    medsTime: null,       // 'morning' | 'evening' | 'both'
    startMode: 'simple',  // 'simple' | 'all' | 'ai'
  });

  // Persist step so onboarding can be resumed if interrupted
  useEffect(() => {
    localStorage.setItem(ONBOARDING_STEP_KEY, step.toString());
  }, [step]);

  const updateConfig = useCallback((key, value) => {
    setConfig(prev => ({ ...prev, [key]: value }));
  }, []);

  const next = useCallback(() => {
    setStep(s => Math.min(s + 1, 6));
  }, []);

  const skip = useCallback(() => {
    next();
  }, [next]);

  const handleComplete = useCallback(() => {
    // Save onboarding config to localStorage
    try {
      const wakeMap = { early: '06:00', normal: '07:30', late: '09:00' };
      const sleepMap = { early: '22:00', normal: '23:00', late: '00:30' };

      const scheduleData = {
        wakeTime: wakeMap[config.wakeTime] || '07:30',
        sleepTime: sleepMap[config.sleepTime] || '23:00',
        challenges: config.challenges,
        startMode: config.startMode,
      };
      localStorage.setItem('adhd_schedule_settings', JSON.stringify(scheduleData));

      // Sync to Firestore
      if (isAuthenticated()) {
        saveScheduleFirestore(scheduleData).catch(() => {});
      }

      // Auto-create medication task if needed
      if (config.takesMeds === true && config.medsTime) {
        const medTasks = [];
        const times = {
          morning: [{ time: '08:00', label: 'Médicaments (matin)' }],
          evening: [{ time: '20:00', label: 'Médicaments (soir)' }],
          both: [
            { time: '08:00', label: 'Médicaments (matin)' },
            { time: '20:00', label: 'Médicaments (soir)' }
          ]
        };
        const tasks = times[config.medsTime] || [];
        tasks.forEach(({ time, label }) => {
          medTasks.push({
            id: `med-${time}-${Date.now()}`,
            task: label,
            category: 'vital',
            time,
            recurring: true,
            duration: 5,
            createdAt: new Date().toISOString(),
            version: 1,
          });
        });

        if (medTasks.length > 0) {
          try {
            const existing = JSON.parse(localStorage.getItem('adhd-planner-custom-tasks') || '[]');
            localStorage.setItem('adhd-planner-custom-tasks', JSON.stringify([...existing, ...medTasks]));
          } catch (e) {}
        }
      }

      localStorage.setItem(ONBOARDING_KEY, 'true');
      localStorage.removeItem(ONBOARDING_STEP_KEY);
    } catch (e) {}

    onComplete?.();
  }, [config, onComplete]);

  return (
    <div className="fixed inset-0 z-[400] bg-zinc-950 flex flex-col items-center justify-center">
      {/* Dots progress */}
      <div className="absolute top-6 left-0 right-0 flex justify-center gap-2">
        {[1, 2, 3, 4, 5, 6].map(i => (
          <div
            key={i}
            className={`h-1.5 rounded-full transition-all duration-300 ${
              i === step ? 'w-6 bg-cyan-400' : i < step ? 'w-2 bg-cyan-700' : 'w-2 bg-zinc-700'
            }`}
          />
        ))}
      </div>

      <div className="w-full max-w-sm px-6">
        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            variants={pageVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.25, ease: 'easeOut' }}
          >
            {step === 1 && <StepWelcome onNext={next} />}
            {step === 2 && <StepRhythm config={config} update={updateConfig} onNext={next} />}
            {step === 3 && <StepChallenges config={config} update={updateConfig} onNext={next} onSkip={skip} />}
            {step === 4 && <StepMeds config={config} update={updateConfig} onNext={next} onSkip={skip} />}
            {step === 5 && <StepStartMode config={config} update={updateConfig} onNext={next} />}
            {step === 6 && <StepReady config={config} onComplete={handleComplete} />}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}

// ─── Step 1: Welcome ──────────────────────────────────────────────────────────

function StepWelcome({ onNext }) {
  return (
    <div className="text-center space-y-8">
      <motion.div
        className="text-6xl"
        animate={{ scale: [1, 1.05, 1] }}
        transition={{ repeat: Infinity, duration: 3 }}
      >
        🧠
      </motion.div>

      <div className="space-y-3">
        <h1 className="text-2xl font-semibold text-zinc-100">
          Bonjour !
        </h1>
        <p className="text-zinc-400 leading-relaxed">
          Je suis ton planificateur TDAH.
        </p>
        <p className="text-zinc-500 text-sm">
          Pas de pression. On va mettre ça en place ensemble, une étape à la fois.
        </p>
      </div>

      <button
        onClick={onNext}
        className="w-full py-4 bg-cyan-500 hover:bg-cyan-400 text-black font-medium rounded-2xl transition-colors flex items-center justify-center gap-2"
      >
        Commencer
        <ChevronRight className="w-5 h-5" />
      </button>
    </div>
  );
}

// ─── Step 2: Rhythm ───────────────────────────────────────────────────────────

function StepRhythm({ config, update, onNext }) {
  return (
    <div className="space-y-6">
      <div className="text-center space-y-2">
        <span className="text-3xl">⏰</span>
        <h2 className="text-xl font-semibold text-zinc-100">Ton rythme</h2>
        <p className="text-zinc-500 text-sm">On adapte ton planning à toi.</p>
      </div>

      <div className="space-y-4">
        <div>
          <p className="text-sm font-medium text-zinc-300 mb-2">Tu te lèves généralement...</p>
          <div className="grid grid-cols-3 gap-2">
            {[
              { id: 'early', label: 'Tôt', sub: 'avant 7h', emoji: '🌅' },
              { id: 'normal', label: 'Normal', sub: '7h–9h', emoji: '☀️' },
              { id: 'late', label: 'Tard', sub: 'après 9h', emoji: '🌤️' },
            ].map(opt => (
              <OptionCard
                key={opt.id}
                selected={config.wakeTime === opt.id}
                onClick={() => update('wakeTime', opt.id)}
                emoji={opt.emoji}
                label={opt.label}
                sub={opt.sub}
              />
            ))}
          </div>
        </div>

        <div>
          <p className="text-sm font-medium text-zinc-300 mb-2">Tu te couches généralement...</p>
          <div className="grid grid-cols-3 gap-2">
            {[
              { id: 'early', label: 'Tôt', sub: 'avant 22h', emoji: '🌙' },
              { id: 'normal', label: 'Normal', sub: '22h–00h', emoji: '🌛' },
              { id: 'late', label: 'Tard', sub: 'après 00h', emoji: '🦉' },
            ].map(opt => (
              <OptionCard
                key={opt.id}
                selected={config.sleepTime === opt.id}
                onClick={() => update('sleepTime', opt.id)}
                emoji={opt.emoji}
                label={opt.label}
                sub={opt.sub}
              />
            ))}
          </div>
        </div>
      </div>

      <button
        onClick={onNext}
        className="w-full py-3.5 bg-cyan-500 hover:bg-cyan-400 text-black font-medium rounded-2xl transition-colors"
      >
        Continuer
      </button>
    </div>
  );
}

// ─── Step 3: Challenges ───────────────────────────────────────────────────────

function StepChallenges({ config, update, onNext, onSkip }) {
  const toggle = useCallback((id) => {
    const current = config.challenges;
    const next = current.includes(id)
      ? current.filter(c => c !== id)
      : current.length < 3 ? [...current, id] : current;
    update('challenges', next);
  }, [config.challenges, update]);

  return (
    <div className="space-y-6">
      <div className="text-center space-y-2">
        <span className="text-3xl">💡</span>
        <h2 className="text-xl font-semibold text-zinc-100">Tes défis</h2>
        <p className="text-zinc-500 text-sm">Qu'est-ce qui est le plus difficile ? <br />Choisis jusqu'à 3.</p>
      </div>

      <div className="grid grid-cols-2 gap-2">
        {CHALLENGES.map(ch => (
          <button
            key={ch.id}
            onClick={() => toggle(ch.id)}
            className={`p-3 rounded-xl border text-left transition-all ${
              config.challenges.includes(ch.id)
                ? 'bg-cyan-500/20 border-cyan-500/60 text-zinc-100'
                : 'bg-zinc-900/60 border-zinc-800 text-zinc-400 hover:border-zinc-600'
            }`}
          >
            <span className="text-xl block mb-1">{ch.emoji}</span>
            <span className="text-xs leading-tight">{ch.label}</span>
            {config.challenges.includes(ch.id) && (
              <Check className="w-3 h-3 text-cyan-400 mt-1" />
            )}
          </button>
        ))}
      </div>

      <div className="flex gap-3">
        <button
          onClick={onSkip}
          className="flex-1 py-3 bg-zinc-800 hover:bg-zinc-700 text-zinc-400 rounded-2xl transition-colors text-sm"
        >
          Passer
        </button>
        <button
          onClick={onNext}
          className="flex-[2] py-3 bg-cyan-500 hover:bg-cyan-400 text-black font-medium rounded-2xl transition-colors"
        >
          Continuer
        </button>
      </div>
    </div>
  );
}

// ─── Step 4: Meds ─────────────────────────────────────────────────────────────

function StepMeds({ config, update, onNext, onSkip }) {
  return (
    <div className="space-y-6">
      <div className="text-center space-y-2">
        <span className="text-3xl">💊</span>
        <h2 className="text-xl font-semibold text-zinc-100">Médicaments</h2>
        <p className="text-zinc-500 text-sm">
          Optionnel. Je créerai des rappels pour toi.
        </p>
      </div>

      {config.takesMeds === null ? (
        <div className="space-y-3">
          {[
            { v: true, label: 'Oui, je prends des médicaments', emoji: '💊' },
            { v: false, label: 'Non', emoji: '🙅' },
            { v: 'skip', label: 'Préfère ne pas répondre', emoji: '🔒' },
          ].map(opt => (
            <button
              key={String(opt.v)}
              onClick={() => {
                update('takesMeds', opt.v);
                if (opt.v !== true) setTimeout(onNext, 300);
              }}
              className="w-full p-4 bg-zinc-900/60 border border-zinc-800 hover:border-zinc-600 text-zinc-300 rounded-xl text-left flex items-center gap-3 transition-colors"
            >
              <span className="text-xl">{opt.emoji}</span>
              <span className="text-sm">{opt.label}</span>
            </button>
          ))}
        </div>
      ) : (
        <div className="space-y-3">
          <p className="text-sm font-medium text-zinc-300">À quelle heure ?</p>
          {[
            { v: 'morning', label: 'Le matin', emoji: '🌅' },
            { v: 'evening', label: 'Le soir', emoji: '🌙' },
            { v: 'both', label: 'Les deux', emoji: '⏱️' },
          ].map(opt => (
            <button
              key={opt.v}
              onClick={() => { update('medsTime', opt.v); setTimeout(onNext, 300); }}
              className="w-full p-4 bg-zinc-900/60 border border-zinc-800 hover:border-cyan-500/40 text-zinc-300 rounded-xl text-left flex items-center gap-3 transition-colors"
            >
              <span className="text-xl">{opt.emoji}</span>
              <span className="text-sm">{opt.label}</span>
            </button>
          ))}
          <button
            onClick={onSkip}
            className="w-full py-3 text-zinc-600 text-sm hover:text-zinc-400 transition-colors"
          >
            Passer cette étape
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Step 5: Start mode ───────────────────────────────────────────────────────

function StepStartMode({ config, update, onNext }) {
  const modes = [
    {
      id: 'simple',
      emoji: '🌱',
      title: 'Commence simple',
      desc: "Seulement l'essentiel d'abord — moins de stress",
    },
    {
      id: 'all',
      emoji: '📋',
      title: 'Tout voir',
      desc: "Toutes les fonctionnalités dès le départ",
    },
    {
      id: 'ai',
      emoji: '✨',
      title: "Laisser l'IA décider",
      desc: 'Génère une semaine de démarrage pour moi',
    },
  ];

  return (
    <div className="space-y-6">
      <div className="text-center space-y-2">
        <Sparkles className="w-8 h-8 text-cyan-400 mx-auto" />
        <h2 className="text-xl font-semibold text-zinc-100">Comment démarrer ?</h2>
      </div>

      <div className="space-y-3">
        {modes.map(mode => (
          <button
            key={mode.id}
            onClick={() => { update('startMode', mode.id); setTimeout(onNext, 200); }}
            className={`w-full p-4 rounded-xl border text-left transition-all ${
              config.startMode === mode.id
                ? 'bg-cyan-500/20 border-cyan-500/60'
                : 'bg-zinc-900/60 border-zinc-800 hover:border-zinc-600'
            }`}
          >
            <div className="flex items-center gap-3">
              <span className="text-2xl">{mode.emoji}</span>
              <div>
                <p className="text-sm font-medium text-zinc-200">{mode.title}</p>
                <p className="text-xs text-zinc-500 mt-0.5">{mode.desc}</p>
              </div>
              {config.startMode === mode.id && (
                <Check className="w-4 h-4 text-cyan-400 ml-auto flex-shrink-0" />
              )}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

// ─── Step 6: Ready ────────────────────────────────────────────────────────────

function StepReady({ config, onComplete }) {
  const summary = [];
  const wakeMap = { early: 'Lève-tôt 🌅', normal: 'Horaire normal ☀️', late: 'Couche-tard 🦉' };
  summary.push(wakeMap[config.wakeTime] || 'Horaire configuré');
  if (config.challenges.length > 0) {
    summary.push(`${config.challenges.length} défi(s) TDAH noté(s)`);
  }
  if (config.takesMeds === true && config.medsTime) {
    summary.push('Rappels médicaments créés 💊');
  }
  const modeMap = { simple: 'Mode simplifié 🌱', all: 'Mode complet 📋', ai: "IA activée ✨" };
  summary.push(modeMap[config.startMode] || '');

  return (
    <div className="text-center space-y-8">
      <motion.div
        className="text-5xl"
        animate={{ scale: [1, 1.2, 1], rotate: [0, 10, -10, 0] }}
        transition={{ duration: 0.6 }}
      >
        🎉
      </motion.div>

      <div className="space-y-3">
        <h2 className="text-2xl font-semibold text-zinc-100">Tu es prêt(e) !</h2>
        <p className="text-zinc-500 text-sm">Voici ce qu'on a configuré :</p>
      </div>

      <div className="bg-zinc-900/60 border border-zinc-800 rounded-xl p-4 space-y-2 text-left">
        {summary.filter(Boolean).map((item, i) => (
          <div key={i} className="flex items-center gap-2">
            <Check className="w-3.5 h-3.5 text-cyan-400 flex-shrink-0" />
            <span className="text-sm text-zinc-300">{item}</span>
          </div>
        ))}
      </div>

      <div className="space-y-3">
        <p className="text-sm text-zinc-500 bg-zinc-900/40 border border-zinc-800 rounded-xl p-3">
          🎯 Premier objectif : <strong className="text-zinc-300">compléter 1 tâche aujourd'hui</strong>
        </p>

        <button
          onClick={onComplete}
          className="w-full py-4 bg-gradient-to-r from-cyan-500 to-blue-500 text-black font-semibold rounded-2xl transition-all hover:from-cyan-400 hover:to-blue-400 flex items-center justify-center gap-2"
        >
          Voir mon planning
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
}

// ─── Shared components ────────────────────────────────────────────────────────

function OptionCard({ selected, onClick, emoji, label, sub }) {
  return (
    <button
      onClick={onClick}
      className={`p-3 rounded-xl border text-center transition-all ${
        selected
          ? 'bg-cyan-500/20 border-cyan-500/60 text-zinc-100'
          : 'bg-zinc-900/60 border-zinc-800 text-zinc-400 hover:border-zinc-600'
      }`}
    >
      <span className="text-2xl block mb-1">{emoji}</span>
      <span className="text-xs font-medium block">{label}</span>
      <span className="text-[10px] text-zinc-500 block">{sub}</span>
      {selected && <Check className="w-3 h-3 text-cyan-400 mx-auto mt-1" />}
    </button>
  );
}

/**
 * Check if onboarding needs to be shown.
 */
export function shouldShowOnboarding() {
  return !localStorage.getItem(ONBOARDING_KEY);
}

export default OnboardingFlow;
